import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PaperPlaneTilt, Trash, Copy, FilePdf } from "@phosphor-icons/react";
import { downloadPdf } from "./ClientsPage";

const empty = { name: "", surname: "", cell: "", email: "", product: "Single", monthly_amount: "", reseller: "Simah" };

const badge = (s) => {
  const map = {
    lead: ["#B7791F", "#FEF3C7"], active: ["#137333", "#D7F5DE"], signed: ["#1A56DB", "#E1EFFE"],
    pending: ["#B7791F", "#FEF3C7"], free: ["#5B6B7E", "#EDEDED"], covered: ["#137333", "#D7F5DE"],
    suspended: ["#B42318", "#FEE4E2"], abandoned: ["#5B6B7E", "#EDEDED"],
  };
  const [fg, bg] = map[s] || ["#5B6B7E", "#EDEDED"];
  return <span style={{ fontSize: 11, fontWeight: 700, color: fg, background: bg, padding: "3px 9px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.04em" }} data-testid="lead-status">{s}</span>;
};

const SOURCE_LABEL = {
  farewell: "came back after cancelling", migration: "from the migration email",
  invite: "from a household invite", website: "found us themselves", app: "signed up in the app",
};

const STAGE_MATCH = {
  sent: () => true,
  opened: (l) => Boolean(l.signup_opened_at || l.signed_at),
  signed: (l) => Boolean(l.signed_at),
  activated: (l) => Boolean(l.app_activated),
};

function Funnel({ leads, stage, onStage }) {
  const stages = [["sent", "Links sent"], ["opened", "Opened"], ["signed", "Signed"], ["activated", "App activated"]];
  const counts = stages.map(([k]) => leads.filter(STAGE_MATCH[k]).length);
  const total = counts[0] || 0;
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-4" data-testid="signup-funnel">
      {stages.map(([k, label], idx) => {
        const n = counts[idx];
        const pct = total ? Math.round((n / total) * 100) : 0;
        const lost = idx > 0 ? counts[idx - 1] - n : 0;
        const on = stage === k;
        return (
          <button key={k} onClick={() => onStage(on ? "" : k)} data-testid={`funnel-stage-${k}`}
            className={`text-left p-4 border transition-colors ${on ? "border-[#007AFF] bg-[#0b1a2b]" : "border-zinc-800 bg-[#121214] hover:bg-zinc-900"}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="text-2xl font-bold mt-1 font-mono-data" data-testid={`funnel-count-${k}`}>{n}</div>
            <div className="text-[11px] mt-1 text-zinc-400">
              {idx === 0 ? "100%" : <>{pct}% of sent{lost > 0 && n > 0 ? <span className="text-amber-400"> · {lost} waiting</span> : null}</>}
            </div>
            <div className="h-1 mt-2 bg-zinc-800"><div className="h-1 bg-[#007AFF]" style={{ width: `${pct}%` }} /></div>
          </button>
        );
      })}
    </div>
  );
}

function SelfStats({ s }) {
  const cards = [["total", "Signed up themselves"], ["paying", "Paying"], ["free", "Still free"], ["from_farewell", "Came back after cancelling"]];
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-4" data-testid="self-signup-stats">
      {cards.map(([k, label]) => (
        <div key={k} className="p-4 border border-zinc-800 bg-[#121214]">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
          <div className="text-2xl font-bold mt-1 font-mono-data" data-testid={`self-stat-${k}`}>{s[k] ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

export default function ClientSignupsPage() {
  const [data, setData] = useState({ leads: [], self_signups: [], self_stats: {}, by_source: {} });
  const [tab, setTab] = useState("self");
  const [meta, setMeta] = useState({ resellers: ["Simah"], products: ["Single", "Couple", "Family"] });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState("");

  const load = () => api.get("/signups/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/resellers").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  const leads = data.leads || [];

  const save = async () => {
    if (!form.name || !form.cell || !form.email) return toast.error("Name, cell and email are required");
    setSaving(true);
    try {
      const payload = { ...form, monthly_amount: form.monthly_amount === "" ? null : parseFloat(form.monthly_amount) };
      const r = await api.post("/client-leads", payload);
      toast.success("Lead created — sign-up link emailed", { description: r.data.email_status === "blocked" ? "Email gated (allowlist only) — copy the link to share manually." : r.data.link });
      setForm(null); setTab("sent"); load();
    } catch (e) { toast.error("Couldn't create lead", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const resend = async (id) => {
    try { const r = await api.post(`/client-leads/${id}/resend`); toast.success("Sign-up link re-sent", { description: r.data.link }); }
    catch (e) { toast.error("Resend failed", { description: e.response?.data?.detail || e.message }); }
  };
  const copyLink = (token) => {
    navigator.clipboard?.writeText(`${window.location.origin}/signup/${token}`);
    toast.success("Sign-up link copied");
  };
  const del = async (id) => { await api.delete(`/client-leads/${id}`); toast.success("Lead removed"); load(); };

  return (
    <div className="p-8 max-w-5xl" data-testid="client-signups-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sign-ups</h1>
          <p className="text-sm text-zinc-400 mt-1">Everyone coming on board — people who signed themselves up in the app (including anyone who took the offer in the farewell email) and retail clients you invited with a debit-order link.</p>
        </div>
        <button onClick={() => setForm({ ...empty, reseller: meta.resellers?.[0] || "Simah" })} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white flex items-center gap-2 shrink-0" data-testid="add-client-signup-button">
          <Plus size={14} weight="bold" /> Add Client
        </button>
      </div>

      <div className="flex gap-2 mt-6 border-b border-zinc-800" data-testid="signups-tabs">
        {[["self", `Signed up themselves (${(data.self_signups || []).length})`], ["sent", `Links you sent (${leads.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`signups-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#007AFF] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {form && (
        <div className="border border-zinc-800 bg-[#121214] p-6 mt-6 grid gap-4 sm:grid-cols-2" data-testid="client-lead-form">
          <label className="text-xs text-zinc-400">Name<input className="tactical-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" /></label>
          <label className="text-xs text-zinc-400">Surname<input className="tactical-input mt-1" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="lead-surname-input" /></label>
          <label className="text-xs text-zinc-400">Cell<input className="tactical-input mt-1" value={form.cell} onChange={(e) => setForm({ ...form, cell: e.target.value })} placeholder="0821234567" data-testid="lead-cell-input" /></label>
          <label className="text-xs text-zinc-400">Email<input className="tactical-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" /></label>
          <label className="text-xs text-zinc-400">Product<select className="tactical-input mt-1" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} data-testid="lead-product-select">{(meta.products || []).map((p) => <option key={p} value={p}>{p}</option>)}</select></label>
          <label className="text-xs text-zinc-400">Monthly amount (R)<input type="number" min="0" step="0.01" className="tactical-input mt-1" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} placeholder="per product" data-testid="lead-amount-input" /></label>
          <label className="text-xs text-zinc-400 sm:col-span-2">Reseller<select className="tactical-input mt-1" value={form.reseller} onChange={(e) => setForm({ ...form, reseller: e.target.value })} data-testid="lead-reseller-select">{(meta.resellers || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
          <div className="flex gap-3 sm:col-span-2">
            <button onClick={save} disabled={saving} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white" data-testid="lead-save-button">{saving ? "Sending…" : "Create & email link"}</button>
            <button onClick={() => setForm(null)} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white">Cancel</button>
          </div>
        </div>
      )}

      {tab === "self" ? (
        <>
          <SelfStats s={data.self_stats || {}} />
          <div className="mt-6 border border-zinc-800" data-testid="self-signups-list">
            {(data.self_signups || []).length === 0 && (
              <div className="p-6 text-sm text-zinc-500 bg-[#121214]">Nobody has signed themselves up yet. Anyone who taps the offer in the farewell email, or installs the app off the website, lands here.</div>
            )}
            {(data.self_signups || []).map((s) => (
              <div key={s.user_id} className="flex items-center justify-between gap-4 p-4 border-b border-zinc-800 last:border-0 bg-[#121214] hover:bg-zinc-900 transition-colors" data-testid={`self-signup-row-${s.user_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-3 flex-wrap">{s.name || "(no name)"} {badge(s.stage)}
                    {s.source === "farewell" && <span className="text-[10px] font-bold uppercase tracking-wider text-green-400 border border-green-900 px-2 py-[2px] rounded-full" data-testid={`self-source-badge-${s.user_id}`}>won back</span>}
                  </div>
                  <div className="font-mono-data text-[11px] text-zinc-400 mt-1 truncate">{s.cell || "no cell"} · {s.email}{s.plan ? ` · ${s.plan}` : ""}{s.price ? ` · R${s.price}` : ""}</div>
                  <div className="font-mono-data text-[10px] text-zinc-500 mt-1 truncate" data-testid={`self-signup-source-${s.user_id}`}>
                    joined {(s.created_at || "").slice(0, 10)} · {SOURCE_LABEL[s.source] || s.source}{s.migrated_member ? " · existing covered member" : ""}
                  </div>
                </div>
                <a href={`/contacts?q=${encodeURIComponent(s.email || s.cell || "")}`} className="text-[11px] font-bold uppercase tracking-wider text-[#007AFF] no-underline shrink-0" data-testid={`self-manage-${s.user_id}`}>manage →</a>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <Funnel leads={leads} stage={stage} onStage={setStage} />
          <div className="mt-6 border border-zinc-800" data-testid="leads-list">
            {(() => { const rows = leads.filter(STAGE_MATCH[stage] || (() => true)); return (<>
              {rows.length === 0 && <div className="p-6 text-sm text-zinc-500 bg-[#121214]">{stage ? "No clients at this stage." : "You haven't sent any sign-up links yet."}</div>}
              {rows.map((l) => (
                <div key={l.id} className="flex items-center justify-between p-4 border-b border-zinc-800 last:border-0 bg-[#121214] hover:bg-zinc-900 transition-colors" data-testid={`lead-row-${l.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-3">{l.name} {l.surname} {badge(l.status)}
                      {l.app_activated && <span className="text-[10px] font-bold uppercase tracking-wider text-green-400 border border-green-900 px-2 py-[2px] rounded-full" data-testid={`lead-activated-${l.id}`}>app active</span>}
                    </div>
                    <div className="font-mono-data text-[11px] text-zinc-400 mt-1 truncate">{l.cell} · {l.email} · {l.product}{l.monthly_amount ? ` · R${l.monthly_amount}` : ""} · {l.reseller}</div>
                    <div className="font-mono-data text-[10px] text-zinc-500 mt-1 truncate" data-testid={`lead-progress-${l.id}`}>
                      {`sent ${(l.created_at || "").slice(0, 10)}`}
                      {l.signup_opened_at ? ` · opened ${l.signup_opened_at.slice(0, 10)}` : " · not opened"}
                      {l.signed_at ? ` · signed ${l.signed_at.slice(0, 10)}` : ""}
                      {l.password_set_at ? ` · activated ${l.password_set_at.slice(0, 10)}` : l.signed_at ? " · app not activated" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {l.status === "active" && (
                      <button onClick={() => downloadPdf(`/client-leads/${l.id}/debit-order.pdf`, `debit-order-${(l.name || "client").toLowerCase().replace(/ /g, "-")}.pdf`).then(() => toast.success("Debit order downloaded")).catch(() => toast.error("Not available — client hasn't signed yet"))} title="Download signed debit order" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-red-400" data-testid={`lead-debitorder-${l.id}`}><FilePdf size={16} /></button>
                    )}
                    <button onClick={() => copyLink(l.signup_token)} title="Copy sign-up link" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-zinc-300" data-testid={`lead-copy-${l.id}`}><Copy size={16} /></button>
                    <button onClick={() => resend(l.id)} title="Resend sign-up email" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-green-400" data-testid={`lead-resend-${l.id}`}><PaperPlaneTilt size={16} /></button>
                    <button onClick={() => del(l.id)} title="Delete" className="p-2 border border-zinc-800 hover:bg-red-500/20 text-red-400 rounded-sm" data-testid={`lead-delete-${l.id}`}><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </>); })()}
          </div>
        </>
      )}
    </div>
  );
}
